import { Component, OnInit, OnDestroy, OnChanges, SimpleChanges, Input } from '@angular/core';
import { UserService } from 'src/app/services/user/user.service';

@Component({
  selector: 'app-conciliation-progress-monitor',
  templateUrl: './conciliation-progress-monitor.component.html',
  styleUrls: ['./conciliation-progress-monitor.component.css']
})
export class ConciliationProgressMonitorComponent implements OnInit, OnDestroy, OnChanges {
  
  @Input() traceType: string = '';
  @Input() directDebitId: string = '';
  @Input() onComplete?: () => void;
  
  traceId: string = '';
  progressData: any = null;
  cobrosData: any = null;
  polling: any = null;
  tracePolling: any = null;
  isProcessing: boolean = true;
  retryCount: number = 0;
  maxRetries: number = 60;
  pollingInterval: number = 15000; // 15 segundos
  traceLookupInterval: number = 10000; // 10 segundos buscando el trace
  isFetching: boolean = false; // Flag para evitar llamadas simultáneas
  isLookingUpTrace: boolean = false; // Flag para evitar búsquedas simultáneas del trace
  completionNotified: boolean = false; // Flag para evitar llamar onComplete más de una vez
  // ¿Vimos el proceso corriendo? Solo entonces "completado" es una novedad que avisar al padre.
  // Abrir un lote que YA estaba conciliado no debe disparar el toast de "conciliación completada".
  sawInProgress: boolean = false;
  
  // Countdown
  countdown: number = 15;
  countdownInterval: any = null;

  // Últimos VALORES de los inputs con los que se inicializó. Se comparan por valor (no por
  // referencia) porque el padre re-asigna current_direct_debit y el binding
  // [onComplete]="...bind(this)" crea una función nueva en cada ciclo de detección de cambios:
  // sin esta comparación, ngOnChanges dispararía re-inicializaciones en bucle.
  private lastTraceType: string | null = null;
  private lastDirectDebitId: string | null = null;
  
  // Estados visuales
  showDetails: boolean = true;
  
  constructor(private userService: UserService) {}
  
  async ngOnInit() {
    // ngOnChanges corre ANTES que ngOnInit y ya inicializa cuando los inputs llegan con valor.
    // Solo se inicializa acá si eso no pasó, para no disparar dos veces el mismo arranque.
    if (this.lastTraceType === null && this.lastDirectDebitId === null) {
      await this.initialize();
    }
  }
  
  async ngOnChanges(changes: SimpleChanges) {
    if (!changes['traceType'] && !changes['directDebitId']) {
      return;
    }

    // Solo se reinicializa si el lote observado CAMBIÓ de verdad. Comparar por valor evita el
    // bucle de re-inicializaciones (y de toasts) que ocurría cuando el padre re-asignaba el
    // mismo lote tras completar, sin dejar pegado el resultado del lote anterior al cambiar
    // de proceso: si el lote es otro, se reinicia todo (incluido completionNotified).
    const mismoLote = this.traceType === this.lastTraceType
                   && this.directDebitId === this.lastDirectDebitId;
    if (mismoLote) {
      return;
    }

    await this.initialize();
  }

  async initialize() {
    // Detener pollings anteriores si existen
    this.stopPolling();
    this.stopTracePolling();
    this.stopCountdown();

    // Resetear estado: TODO lo que se ve en pantalla pertenece al lote anterior.
    this.lastTraceType = this.traceType;
    this.lastDirectDebitId = this.directDebitId;
    this.traceId = '';
    this.progressData = null;
    this.cobrosData = null;
    this.completionNotified = false;
    this.sawInProgress = false;
    this.retryCount = 0;
    this.isProcessing = true;

    // Buscar el trace por tipo
    await this.findTrace();

    if (!this.traceId) {
      // No hay proceso activo: mostrar estado vacío y seguir buscando en background
      this.isProcessing = false;
      this.startTracePolling();
      return;
    }

    // Iniciar polling de progreso
    this.fetchProgress();
    this.startPolling();
  }
  
  async findTrace() {
    try {
      const result = await this.userService.traceDocumentsGet({ type: this.traceType });
      
      if (result && result.resp && result.resp.length > 0) {
        // El backend retorna 'code' en lugar de '_id'
        this.traceId = result.resp[0].code || result.resp[0]._id || result.resp[0].id;
      }
    } catch (error) {
      console.error('Error buscando trace:', error);
    }
  }
  
  ngOnDestroy() {
    this.stopPolling();
    this.stopTracePolling();
    this.stopCountdown();
  }

  /**
   * Polling para detectar cuándo inicia el proceso (cuando aún no hay trace)
   */
  startTracePolling() {
    this.stopTracePolling();
    this.tracePolling = setInterval(async () => {
      if (this.isLookingUpTrace || this.traceId) {
        return;
      }
      this.isLookingUpTrace = true;
      try {
        await this.findTrace();
        if (this.traceId) {
          this.stopTracePolling();
          this.isProcessing = true;
          this.fetchProgress();
          this.startPolling();
        }
      } finally {
        this.isLookingUpTrace = false;
      }
    }, this.traceLookupInterval);
  }

  stopTracePolling() {
    if (this.tracePolling) {
      clearInterval(this.tracePolling);
      this.tracePolling = null;
    }
  }
  
  startCountdown() {
    this.countdown = 15;
    this.countdownInterval = setInterval(() => {
      this.countdown--;
      if (this.countdown <= 0) {
        this.countdown = 15;
      }
    }, 1000);
  }
  
  stopCountdown() {
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
      this.countdownInterval = null;
    }
  }

  /**
   * Método público para refrescar manualmente el progreso.
   * OJO: solo re-consulta el trace que ya estaba siendo observado. Si el proceso se reinició
   * o canceló desde afuera, el trace viejo sigue devolviendo su último estado (ej: ERROR) y la
   * vista queda pegada: para ese caso usar reload().
   */
  public refresh() {
    if (this.traceId) {
      this.fetchProgress();
    }
  }

  /**
   * Reinicio completo del monitor: olvida el proceso observado, limpia lo que hay en pantalla
   * y vuelve a buscar si el lote tiene un trace activo. Es lo que hay que llamar cuando el
   * proceso se reinició/canceló desde afuera, para no depender de un refresh del navegador.
   */
  public async reload() {
    await this.initialize();
  }
  
  /**
   * Inicia el polling para consultar el progreso
   */
  startPolling() {
    // Consultar inmediatamente
    this.fetchProgress();
    
    // Iniciar countdown
    this.startCountdown();
    
    // Luego cada 15 segundos
    this.polling = setInterval(() => {
      this.fetchProgress();
      this.countdown = 15; // Reiniciar countdown
    }, this.pollingInterval);
  }
  
  /**
   * Detiene el polling
   */
  stopPolling() {
    if (this.polling) {
      clearInterval(this.polling);
      this.polling = null;
    }
  }
  
  /**
   * Consulta el progreso del backend
   */
  async fetchProgress() {
    // Evitar llamadas simultáneas
    if (this.isFetching) {
      console.log('⏸️ Ya hay una llamada en progreso, saltando...');
      return;
    }
    
    this.isFetching = true;
    
    try {
      const response = await this.userService.getConciliationProgress(this.traceId);
      
      console.log('📥 Respuesta recibida, state:', response.state);
      
      this.progressData = response;
      this.retryCount = 0;

      // Obtener datos de cobros si tenemos directDebitId
      if (this.directDebitId) {
        try {
          const cobrosResponse = await this.userService.getDirectDebitDetails(this.directDebitId, 1, 1);
          if (cobrosResponse?.resp?.counts) {
            this.cobrosData = cobrosResponse.resp.counts;
          }
        } catch {
          // Si falla, no bloqueamos el progreso principal
        }
      }
      
      if (response.state === 'COMPLETE') {
        this.isProcessing = false;
        this.stopPolling();
        this.stopCountdown();

        // Solo se avisa al padre si vimos el proceso corriendo: al abrir un lote ya conciliado
        // la vista muestra el resultado, pero sin el toast de "recién completado".
        if (this.onComplete && !this.completionNotified && this.sawInProgress) {
          this.completionNotified = true;
          this.onComplete();
        }
        return;
      }
      
      if (response.state === 'ERROR') {
        this.isProcessing = false;
        this.stopPolling();
        return;
      }
      
      if (response.state === 'CANCELLED' || response.state === 'CANCELLED_MULTIPLE_EXECUTION') {
        this.isProcessing = false;
        this.stopPolling();
        return;
      }
      
      if (response.status === false) {
        this.isProcessing = false;
        this.stopPolling();
        return;
      }

      this.isProcessing = true;
      this.sawInProgress = true; // el proceso está corriendo ahora mismo
      
    } catch (error: any) {
      console.error('❌ Error en fetchProgress:', error);
      if (this.retryCount < this.maxRetries) {
        this.retryCount++;
      } else {
        this.stopPolling();
        this.isProcessing = false;
      }
    } finally {
      // Siempre liberar el flag
      this.isFetching = false;
    }
  }
  
  /**
   * Calcula el porcentaje de pagos cobrados
   */
  getSuccessPercentage(): number {
    if (!this.progressData?.progress?.total || this.progressData.progress.total === 0) {
      return 0;
    }
    return (this.progressData.progress.successful / this.progressData.progress.total) * 100;
  }

  /**
   * Calcula el porcentaje de pagos sin cobrar
   */
  getSkippedPercentage(): number {
    if (!this.progressData?.progress?.total || this.progressData.progress.total === 0) {
      return 0;
    }
    return (this.progressData.progress.skipped / this.progressData.progress.total) * 100;
  }

  /**
   * Calcula el porcentaje de cobros realizados
   */
  getCobrosPercentage(): number {
    if (!this.cobrosData?.total || this.cobrosData.total === 0) {
      return 0;
    }
    return Math.round((this.cobrosData.completed / this.cobrosData.total) * 100);
  }

  /**
   * Calcula el total de no cobrados (solo errores)
   */
  getNoCobradosTotal(): number {
    if (!this.cobrosData) {
      return 0;
    }
    return this.cobrosData.error || 0;
  }

  /**
   * Calcula el stroke-dasharray para el gráfico de pie (cobrados)
   */
  getCobradosDashArray(): string {
    const percentage = this.getCobrosPercentage();
    const circumference = 2 * Math.PI * 40; // radio = 40
    const filled = (percentage / 100) * circumference;
    const empty = circumference - filled;
    return `${filled} ${empty}`;
  }

  /**
   * Calcula el stroke-dasharray para no cobrados
   */
  getNoCobradosDashArray(): string {
    const percentage = 100 - this.getCobrosPercentage();
    const circumference = 2 * Math.PI * 40;
    const filled = (percentage / 100) * circumference;
    const empty = circumference - filled;
    return `${filled} ${empty}`;
  }

  /**
   * Calcula el offset para el segmento de no cobrados
   */
  getNoCobradosOffset(): number {
    const percentage = this.getCobrosPercentage();
    const circumference = 2 * Math.PI * 40;
    const cobradosLength = (percentage / 100) * circumference;
    return 25 - cobradosLength; // 25 es el offset inicial
  }

  /**
   * Calcula el tiempo transcurrido desde el último pago
   */
  getTimeSinceLastPayment(): string {
    if (!this.progressData?.last_payment?.date) {
      return 'N/A';
    }
    
    const lastPaymentDate = new Date(this.progressData.last_payment.date);
    const now = new Date();
    const diffMs = now.getTime() - lastPaymentDate.getTime();
    const diffMinutes = Math.floor(diffMs / 60000);
    
    if (diffMinutes < 1) {
      return 'Menos de 1 minuto';
    } else if (diffMinutes === 1) {
      return '1 minuto';
    } else if (diffMinutes < 60) {
      return `${diffMinutes} minutos`;
    } else {
      const hours = Math.floor(diffMinutes / 60);
      const minutes = diffMinutes % 60;
      return `${hours}h ${minutes}m`;
    }
  }

  /**
   * Verifica si se debe mostrar el último pago
   * Solo se muestra cuando processed > skipped (ya pasamos donde nos quedamos)
   */
  shouldShowLastPayment(): boolean {
    if (!this.progressData?.progress) {
      return false;
    }
    
    const processed = this.progressData.progress.processed || 0;
    const skipped = this.progressData.progress.skipped || 0;
    
    return processed > skipped;
  }

  /**
   * Verifica si el último pago fue hace más de 10 minutos y el progreso no es 100%
   */
  isLastPaymentStale(): boolean {
    if (!this.progressData?.last_payment?.date) {
      return false;
    }
    
    const percentage = this.progressData.progress?.percentage || 0;
    if (percentage >= 100) {
      return false;
    }
    
    const lastPaymentDate = new Date(this.progressData.last_payment.date);
    const now = new Date();
    const diffMs = now.getTime() - lastPaymentDate.getTime();
    const diffMinutes = Math.floor(diffMs / 60000);
    
    return diffMinutes > 10;
  }

  /**
   * Obtiene el icono según el estado del pago
   */
  getPaymentStatusIcon(status: string): string {
    switch (status) {
      case 'success': return '✅';
      case 'error': return '❌';
      case 'skipped': return '⏭️';
      case 'processing': return '🔄';
      default: return '⚪';
    }
  }
  
  /**
   * Obtiene la clase CSS según el estado del pago
   */
  getPaymentStatusClass(status: string): string {
    switch (status) {
      case 'success': return 'status-success';
      case 'error': return 'status-error';
      case 'skipped': return 'status-skipped';
      case 'processing': return 'status-processing';
      default: return '';
    }
  }
  
  /**
   * Formatea la fecha de manera legible
   */
  formatDate(date: string | Date): string {
    if (!date) return '';
    const d = new Date(date);
    return d.toLocaleTimeString('es-VE', { 
      hour: '2-digit', 
      minute: '2-digit', 
      second: '2-digit' 
    });
  }
  
  /**
   * Formatea el monto
   */
  formatAmount(amount: number): string {
    if (!amount) return '0.00';
    return amount.toFixed(2);
  }
  
  /**
   * Toggle para mostrar/ocultar detalles
   */
  toggleDetails() {
    this.showDetails = !this.showDetails;
  }
  
  /**
   * Obtiene el color de la barra de progreso
   */
  getProgressBarColor(): string {
    if (!this.progressData?.progress) return '#3b82f6';
    
    const percentage = this.progressData.progress.percentage;
    if (percentage < 30) return '#3b82f6'; // Azul
    if (percentage < 70) return '#f59e0b'; // Amarillo
    return '#10b981'; // Verde
  }
}
